import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CloudUploadOutlined,
  EditFilled,
  LoadingOutlined,
  StarFilled,
} from "@ant-design/icons";
import {
  Checkbox,
  Col,
  Input,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Spin,
  Tooltip,
} from "antd";
import * as axios from "axios";
import moment from "moment";
import { nanoid } from "nanoid";

import React, {
  useCallback,
  useEffect,
  useGlobal,
  useMemo,
  useRef,
  useState,
} from "reactn";
import call from "../call";
import { Clipboard } from './_common/Clipboard';
import Button from "./button";
import { ReactComponent as CopyIcon } from "./icons/copy.svg";
import { ReactComponent as DownloadsIcon } from "./icons/Downloads.svg";
import Text from "./text";

/**
 * @typedef DownloadProps
 * @type Object
 * @property {boolean} open Should show downloads dialog
 * @property {Object} version
 * @property {JSX.Element} children
 * @property {string} playable_id
 * @property {function()} onClose
 */

let map = {
  aarki: "html,copy",
  adcolony: "copy,html",
  applovin: "copy,html",
  appreciate: "zip",
  chartboost: "copy,html",
  criteo: "zip",
  crossinstall: "copy,html",
  default: "copy",
  fb: "zip,html",
  fyber: "copy,html",
  google: "zip",
  google360: "zip",
  ironsource: "copy,html",
  kayzen: "apikey",
  kaden: "html",
  kidoz: "html",
  lifestreet: "copy,html",
  liftoff: "copy,zip",
  mobvista: "html",
  moloco: "html,molocotest",
  nucleo: "copy,html",
  oceanengine: "copy,zip",
  pointblank: "html",
  smadex: "zip",
  snapchat: "zip",
  tapjoy: "copy,html",
  tencent: "zip",
  toutiao: "copy,zip",
  toutiao_cn: "copy,zip",
  unity: "copy,html",
  voodoo: "copy,html",
  voodoo_notion: "html",
  vungle: "html",
  web: "copy,html",
  yandex: "zip",
  zucks: "copy,html",
  instantGame: "html",
  adikteev:"html",
  luna:"html"
};
const dataTrackingNetworks = [
  "adcolony",
  "applovin",
  "criteo",
  "ironsource",
  "kayzen",
  "mobvista",
  "moloco",
  "unity",
  "vungle",
];

let adNetworks = [
  "aarki",
  "adcolony",
  "applovin",
  "appreciate",
  "chartboost",
  "criteo",
  "crossinstall",
  "default",
  "fb",
  "fyber",
  "google",
  "google360",
  "ironsource",
  "kaden",
  "kayzen",
  "kidoz",
  "lifestreet",
  "liftoff",
  "mobvista",
  "moloco",
  "nucleo",
  "oceanengine",
  "pointblank",
  "smadex",
  "snapchat",
  "tapjoy",
  "tencent",
  "toutiao",
  "toutiao_cn",
  "unity",
  "voodoo",
  "voodoo_notion",
  "vungle",
  "web",
  "yandex",
  "zucks",
  "instantGame",
  "adikteev",
  "luna"
];

let networkPrettyNames = {
  "adcolony": "AdColony",
  "applovin": "Applovin",
  "chartboost": "Chartboost",
  "fb": "Facebook",
  "google": "Google",
  "ironsource": "IronSource",
  "liftoff": "Liftoff",
  "mobvista": "Mintegral",
  "moloco": "Moloco",
  "tencent": "Tencent",
  "toutiao": "TikTok",
  "unity": "Unity",
  "vungle": "Vungle",
  "instantGame":"Instant Game",
  "adikteev":"Adikteev",
  "luna":"Luna"
};

const NODE_ENV = process.env.NODE_ENV;
let host = (window.host =
  NODE_ENV === "development"
    ? process.env.REACT_APP_API_HOST
    : "https://gearbox-old.playablefactory.com");

/**
 *
 * @param {DownloadProps} props
 * @return {JSX.Element}
 * @constructor
 */
let Download = (props) => {
  let [user] = useGlobal("user");
  let [version, setVersion] = useState(null);
  let [show, setShow] = useState(props.open);
  let [downloading, setDownloading] = useState(false);
  let [, setGame] = useState(null);
  let [playable, setPlayable] = useState(null);
  let [nano, setNano] = useState(nanoid(5));
  let [multi, setMulti] = useState({});
  let [more, setMore] = useState(true);
  let [versions, setVersions] = useState(null);
  let [selected, setSelected] = useState(null);
  let [scroll, setScroll] = useState(0);
  let [source, setSource] = useState(null);
  let [size, setSize] = useState(0);
  let [brotli, setBrotli] = useState(false);
  let [page, setPage] = useState(0);
  let list = useRef();
  let [, setRefresh] = useState(0);
  const [kayzenApiKeyAvailable, setKayzenApiKeyAvailable] = useState(null);

  useEffect(() => {
    let n = nanoid(5);
    setNano(n);
    if (version?._id == null) return;

    let run = async () => {
      if (selected === "default") {
        return message.success("Link copied");
      }

      let { url, size, brotli } = await call("createPlayableLink", {
        _id: version?._id,
        network: "default",
        nano: "test",
        noanalytic: true,
      });
      setSize(size);
      setBrotli(brotli);
      console.log({ size, brotli });
      let source;
      if (process.env.NODE_ENV === "development")
        source = await fetch(
          window.host + "/files/" + url + "?t" + nanoid()
        ).then((x) => x.text());
      else
        source = await fetch(
          window.host + "/files/" + url + "?t" + nanoid()
        ).then((x) => x.text());
      setSource(source);
    };

    run()
      .then(() => { })
      .catch(console.log);
  }, [version]);

  let createCopyLink = async (v, name, addon, download, orientation) => {
    try {
      if (version == null) return message.error("version is null");
      await call("createPlayableLink", {
        _id: version?._id,
        nano,
        network: selected,
        orientation,
        zip: false,
        analytics: dataTrackingNetworks.includes(selected) ? 1 : 0,
        additional: addon,
      });
    } catch (err) {
      if (err.message === "You have reached your download limit") {
        message.error("You have reached your download limit");
      } else {
        message.error("An error happen when downloading");
      }
    }
  };

  let multidownload = async () => {
    try {
      let networks = Object.keys(multi).filter((n) => multi[n]);
      if (networks.length === 0)
        return message.warn("Please select at least one network to download");

      setDownloading(true);
      let { url } = await call("createPlayableLinkMulti", {
        _id: version?._id,
        networks,
      });
      let element = document.createElement("iframe");
      element.setAttribute("src", window.host + "/files/" + url);

      element.style.display = "none";
      document.body.appendChild(element);
      setDownloading(false);
    } catch (err) {
      setDownloading(false);
      console.log(err);
    }
  };
  let uploadToAdNetwork = async () => {
    //if(selected === 'unity') ---> uploadToUnity
    //{}
    //
    return console.log("test");
  };

  let download = async (v, name, addon, download, orientation, zip) => {
    try {
      if (user == null)
        return message.warn("Test accounts don't have permission to download");
      if (
        user?.customer === "customer:3" ||
        user.customer === "I04ZM1-CZBwK_RwLY5XiLsTjO"
      )
        return message.warn("Test accounts don't have permission to download");
      if (user.role === "customer" && user.noDownload)
        return message.warn("This account don't have permission to download");
      if (version == null) return message.error("version is null");
      setDownloading(true);
      let { url } = await (async () => {
        try {
          return await call("createPlayableLink", {
            _id: version?._id,
            nano,
            network: selected,
            orientation,
            zip,
            analytics: dataTrackingNetworks.includes(selected) ? 1 : 0,
            additional: { ...addon, molocoS3: true },
          });
        } catch (err) {
          return {};
        }
      })();

      let element;
      if (
        selected === "google" ||
        [
          "appreciate",
          "criteo",
          "google360",
          "kayzen",
          "liftoff",
          "mobvista",
          "oceanengine",
          "smadex",
          "snapchat",
          "tencent",
          "toutiao",
          "toutiao_cn",
          "voodoo_notion",
          "vungle",
          "yandex",
        ].includes(selected) ||
        (selected === "fb" && zip)
      ) {
        element = document.createElement("iframe");
        console.log(url);

        element.setAttribute("src", window.host + "/files/" + url);

        element.style.display = "none";
        document.body.appendChild(element);
        element.click();
      } else if (download) {
        let source;
        source = await fetch(window.host + "/files/" + url).then((x) =>
          x.text()
        );

        element = document.createElement("a");

        if (selected === "moloco") {
          element.setAttribute(
            "href",
            "data:text/plain;charset=utf-8," + encodeURIComponent(source)
          );
        } else {
          element.setAttribute(
            "href",
            "data:html/plain;charset=utf-8," + encodeURIComponent(source)
          );
        }
        element.setAttribute(
          "download",
          url.split("/").reduce((_, v) => v, "")
        );

        element.style.display = "none";
        document.body.appendChild(element);
        element.click();
      } else if (url) {
        message.success("Link copied");
      }
      setDownloading(false);
      return url;
    } catch (err) {
      setDownloading(false);
      if (err.message === "You have reached your download limit") {
        message.error("You have reached your download limit");
      } else {
        message.error("An error happen when downloading");
      }
    }
  };

  useEffect(() => {
    if (show) {
      if (props.version) {
        setVersion(props.version);
        console.log(props.version);
        call("findPlayable", {
          _id: props.version.playable?._id || props.version.playable,
        })
          .then((playable) => {
            if (playable.networks === null) {
              playable.networks = dataTrackingNetworks;
            }
            return playable;
          })
          .then(setPlayable);
      }
    } else {
      setVersions(null);
      setVersion(null);
      setPlayable(null);
      if (props.onClose) props.onClose();
    }
  }, [show, props.version]);

  let showVersions = useMemo(() => {
    if (versions == null) return [];
    if (versions && scroll) {
      return versions.slice(scroll, scroll + 3);
    } else {
      if (versions) return versions.slice(0, 3);
      else return [];
    }
  }, [versions, scroll]);
  let totalpage = versions
    ? Math.floor(versions.length / 3) + (versions.length % 3 === 0 ? 0 : 1)
    : 1;
  let currentpage = Math.floor(scroll / 3) + 1;

  useEffect(() => {
    if (version)
      call("findGame", { _id: version.game._id || version.game })
        .then((game) => setGame(game))
        .catch((err) => console.log(err));
    else setGame(null);
  }, [version]);

  useEffect(() => {
    if (version == null) return;
    if (show && versions == null) {
      let query = {
        game: version?.game?._id || version.game,
        deleted: { $ne: true },
      };
      if (props.playable) query.playable = props.playable._id;
      call("findPlayableVersions", {
        query: query,
        sort: { updatedAt: -1 },
        populate: { playable: true },
        size: 10,
        page: page,
        fields: { _id: 1, name: 1, updatedAt: 1 },
      }).then((x) => {
        if (props?.version?._id == null && x.rows.length > 0) {
          setVersion(x.rows[0]);
        }
        setVersions(x.rows);
      });
    }
  }, [show, version, props, page]);

  useEffect(() => {
    window.addEventListener("resize", setRefresh);
    return () => window.removeEventListener("resize", setRefresh);
  }, []);

  useEffect(() => {
    if (!show) {
      setVersion(null);
      setDownloading(false);
      setGame(null);
      setPlayable(null);
      setNano(nanoid(5));
      setMulti({});
      setVersions(null);
      setSelected(null);
      setScroll(0);
      setSource(null);
      setSize(0);
      setBrotli(false);
      setMore(false);
    }
  }, [show]);

  useEffect(() => {
    if (playable) {
      call("isKayzenApiKeyAvailable", {
        playableId: playable._id,
      })
        .then((x) => {
          console.log("isKayzenApiKeyAvailable", x);
          return x;
        })
        .then((x) => setKayzenApiKeyAvailable(x.result));
    }
  }, [playable, setKayzenApiKeyAvailable]);

  const copyLink = useMemo(() => {
    if (!version) {
      return undefined;
    }
    const defaultLink = `${window.location.origin}/test?version=${version._id}`;
    const removeSpecials = (s) => s.replace(/[^\w-]/g, "_");
    const safeId = removeSpecials(`${version._id}_${nano}`) // there are some old ids that use version:# syntax
    const safeFileName = removeSpecials(version.name) // version names can contain special characters such as !/
    // i do not like to talk about the security implications of this
    const suffix ="";// dataTrackingNetworks.includes(selected) ? "_beta" : ""
    let urlNetworkName = selected;
    if (networkPrettyNames[selected])
      urlNetworkName = networkPrettyNames[selected];
    const fileLink = `${window.host}/files/${safeId}/${safeFileName}_${urlNetworkName}${suffix}.html`
    return selected === "default" ? defaultLink : fileLink;
  }, [selected, version, nano]);

  return (
    <>
      <div onClick={() => setShow(true)}>{props.children}</div>
      {show && (
        <>
          <div
            className={
              "fixed left-0 top-0 w-full z-[100] h-full bg-[#00000040]"
            }
            onClick={() => setShow(false)}
          />
          <div
            className={`smallscroll fixed z-[120] left-0 top-0 p-0 rounded-2xl bg-white flex ml-[50vw] mt-[50vh] w-[1195px] h-[800px] ${window.innerHeight < 840
              ? "scale-90 -translate-x-2/4 -translate-y-2/4"
              : "-translate-x-2/4 -translate-y-2/4"
              }`}
          >
            {/* Yan taraf hepsi */}
            <div className="flex p-4 flex-col">
              {/* Yan tarafin oyunun demosunun gorundugu yer */}
              <div className="w-full flex justify-center">
                {source && (
                  <iframe
                    title="preview"
                    srcDoc={source} // TODO: src ile direk link vererek yapilabilir
                    className="border-none rounded-lg	w-[300px] h-[500px]"
                  />
                )}
                {!source && (
                  <div className="rounded-3xl w-[300px] h-[500px] border-1 border-solid border-[#808080] items-center flex justify-center">
                    <Spin />
                  </div>
                )}
              </div>
              {/* Yan tarafin oyunun demosunun gorundugu yer */}

              {/* yan taraf uclu versiyonlar gozukuyor */}
              <div className="h-[200px]">
                <div ref={list} className="nscroll overflow-y-auto mt-5">
                  {showVersions
                    .slice(
                      0,
                      Math.max(3, Math.floor((window.innerHeight * 0.2) / 50))
                    )
                    .map((v) => (
                      <div
                        className={`hovergrey cursor-pointer flex justify-between border-solid border-t-0 border-r-0 border-l-0 border-b border-[#c1c1c1c0] h-10 items-center ${window.innerWidth < 1000 ? "w-[180px]" : "w-[300px]"
                          }`}
                        style={{
                          background:
                            v._id === version?._id ? "#f1f1f1" : "inherit",
                        }}
                        key={v._id}
                        onClick={() => {
                          setVersion(v);
                        }}
                      >
                        <Text grey fontSize={12}>
                          {v.name.length > 24
                            ? v.name.slice(0, 20) + "..."
                            : v.name}
                        </Text>
                        <div className="w-[100px]">
                          <Text grey fontSize={12}>
                            {moment(v.updatedAt).format("DD MMM YYYY")}
                          </Text>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
              {/* yan taraf uclu versiyonlar gozukuyor */}

              {/* yan taraf uclu versiyon pagination */}
              <div className="mt-4">
                <button
                  className="shadow hovergrey border-none bg-[#eee] w-9 h-9 rounded-[30px] cursor-pointer text-lg"
                  onClick={() => scroll > 0 && setScroll(scroll - 3)}
                >
                  <ArrowUpOutlined className="txt-yellow" />
                </button>
                <button
                  className="shadow hovergrey border-none bg-[#eee] w-9 h-9 rounded-[30px] cursor-pointer text-lg ml-5"
                  onClick={() =>
                    versions?.length - 3 > scroll && setScroll(scroll + 3)
                  }
                >
                  <ArrowDownOutlined className="txt-yellow" />
                </button>
                <div className="float-right	mt-1">
                  <Text grey fontSize={14}>
                    {`Page ${currentpage} /${totalpage} `}
                  </Text>
                </div>
              </div>
              {/* yan taraf uclu versiyon pagination */}
            </div>
            {/* Yan taraf hepsi */}

            <div className="w-full flex-col justify-center bg-[#f5f8fb]">
              {/* carpi butonu ve select network yazisi */}
              <div className="p-4 mb-0 border-l-2 border-r-0 border-b-0 border-t-0 border-solid border-[#c1c1c1c0]">
                <span className="float-right mr-[-10px] mt[-10px]">
                  <Button
                    left="/x.svg"
                    width={36}
                    height={36}
                    noimgbg
                    small
                    onClick={() => setShow(false)}
                  />
                </span>
                <Text blue fontSize={22} bold>
                  Select a Network <Text className="txt-dark">From List</Text>
                </Text>
              </div>
              {/* carpi butonu ve select network yazisi */}

              <div className="flex flex-1 items-center w-full justify-center bg-[#f5f8fb] p-4 pt-1 border-l-2 border-r-0 border-b-0 border-t-0 border-solid border-[#c1c1c1c0]">
                <div className="flex flex-col flex-wrap p-2 pt-0	pb-0">
                  {/* Mobile ve multiple yazan kisim */}
                  {!playable && <Spin />}
                  {playable && version && (
                    <div className="flex flex-wrap	mt-2">
                      <div className="flex w-full">
                        <div
                          className={`${selected === "default" ? "bg-[#f5ede9]" : "bg-white"
                            } h-14 flex-1 rounded-lg	mr-1 mb-2 text-sm	cursor-pointer p-2 flex items-center`}
                          onClick={() => setSelected("default")}
                        >
                          <div className="flex justify-between p-0 ml-2 items-center">
                            <img
                              src="/mobile.svg"
                              alt=""
                              className="h-[30px]"
                            />
                            <div className="ml-[30px] flex-1">
                              <Text blue fontSize={16} bold>
                                Mobile <Text className="txt-dark">Testing</Text>
                              </Text>
                              <div>
                                <Text fontSize={14} grey2>
                                  Use the link on any browser to test
                                </Text>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div
                          className={`${selected === "multi" ? "bg-[#f5ede9]" : "bg-white"
                            } h-14 flex-1 rounded-lg mr-0 ml-1 mb-2 text-sm	cursor-pointer p-2 flex items-center`}
                          onClick={() =>
                            selected !== "multi"
                              ? setSelected("multi")
                              : setSelected("default")
                          }
                        >
                          <div className="flex justify-between p-0 w-[300px] items-center">
                            <img src="/multi.png" alt="" className="h-[30px]" />
                            <div className="ml-[30px] flex-1">
                              <Text blue fontSize={16} bold>
                                Multiple
                              </Text>

                              <div>
                                <Text fontSize={16} dark>
                                  Downloads
                                </Text>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Mobile ve multiple yazan kisim */}

                  <Row className="w-full" gutter={[8, 8]}>
                    <Col span={24}>
                      <Text blue fontSize={18} bold>
                        Networks
                      </Text>
                    </Col>
                    {playable !== null &&
                      adNetworks
                        .filter((e) => playable.networks.includes(e))
                        .filter((e) =>
                          e === "voodoo"
                            ? playable.customer === "customer::20"
                            : true
                        )
                        .map((e, i) => (
                          <Col span={8} key={e}>
                            <PlatformButton
                              key={i}
                              index={i}
                              onClick={() => {
                                if (selected === "multi") {
                                  setMulti({ ...multi, [e]: !multi[e] });
                                } else {
                                  playable &&
                                    (playable?.networks == null ||
                                      playable.networks.find((n) => n === e)) &&
                                    setSelected(e);
                                }
                              }}
                              isMulti={selected === "multi"}
                              isSelected={
                                selected === e ||
                                (selected === "multi" && multi[e])
                              }
                              adNetworks={adNetworks}
                              network={e}
                            />
                          </Col>
                        ))}
                  </Row>
                  <Text
                    blue
                    fontSize={16}
                    bold
                    className={"block text-center mt-[5%]"}
                  >
                    <StarFilled className="text-l text-[#2B5ECA]" /> Data
                    tracking functionality is turned on by default for marked
                    network downloads
                  </Text>
                </div>
              </div>

              {!downloading && (
                <div className="h-[100px] flex items-center justify-center border-t-2 border-r-0 border-l-2 border-b-0 border-solid border-[#c1c1c1c0] bg-[#f5f8fb]">
                  {"unity".includes(selected) && (
                    <ShowSelectionUnityCreative
                      playableId={playable._id}
                      gameId={playable.game}
                      defaultAppleAppId={playable.unityAppleAppId}
                      defaultGoogleAppId={playable.unityGoogleAppId}
                      versionId={version._id}
                      customerId={user.customer}
                    />
                  )}
                  {/* {"applovin".includes(selected) && (
                    <ShowSelectionApplovinCreative
                      playableId={playable._id}
                      gameId={playable.game}
                      versionId={version._id}
                      defaultAppleLink={playable.applovinAppleLink}
                      defaultGoogleLink={playable.applovinGoogleLink}
                    />
                  )} */}
                  {map[selected]?.indexOf("landscape") >= 0 && (
                    <DownloadButton
                      onClick={() =>
                        download(
                          version,
                          selected,
                          undefined,
                          true,
                          "landscape",
                          true
                        )
                      }
                    >
                      Download Landscape
                    </DownloadButton>
                  )}

                  {map[selected]?.indexOf("portrait") >= 0 && (
                    <DownloadButton
                      onClick={() =>
                        download(
                          version,
                          selected,
                          undefined,
                          true,
                          "portrait",
                          true
                        )
                      }
                    >
                      Download Portrait
                    </DownloadButton>
                  )}

                  {map[selected]?.indexOf("html") >= 0 && (
                    <DownloadButton
                      onClick={() =>
                        download(version, selected, undefined, true)
                      }
                    >
                      Download
                    </DownloadButton>
                  )}
                  {user.customer == "playableFactoryInternal" && map[selected]?.indexOf("molocotest") >= 0 && (
                    <>
                      <DownloadMolocoTest
                        versionId={version._id}
                        type={"Applovin"}
                      ></DownloadMolocoTest>
                      <DownloadMolocoTest
                        versionId={version._id}
                        type={"Chartboost"}
                      ></DownloadMolocoTest>
                      {/*<DownloadMolocoTest versionId={version._id} type={"Fyber"}></DownloadMolocoTest>*/}
                      {/*<DownloadMolocoTest versionId={version._id} type={"Unity"}></DownloadMolocoTest>*/}
                    </>
                  )}
                  {map[selected]?.indexOf("zip") >= 0 &&
                    // (selected === "fb" ? (
                    //   <Popconfirm
                    //     title={
                    //       "Please contact with us for Facebook whitelisting process."
                    //     }
                    //     onConfirm={() =>
                    //       download(
                    //         version,
                    //         selected,
                    //         undefined,
                    //         true,
                    //         null,
                    //         true
                    //       )
                    //     }
                    //   >
                    //     <DownloadButton onClick={() => {}}>
                    //       Download as Zip
                    //     </DownloadButton>
                    //   </Popconfirm>
                    // ) : 
                    (
                      <DownloadButton
                        onClick={() =>
                          download(
                            version,
                            selected,
                            undefined,
                            true,
                            null,
                            true
                          )
                        }
                      >
                        Download as Zip
                      </DownloadButton>
                      //)
                    )}

                  {map[selected]?.indexOf("apikey") >= 0 && (
                    <InputButton
                      placeHolder={"Kayzen Api Key"}
                      onClick={(val) => {
                        if (val) {
                          if (!playable?._id) {
                            return;
                          }
                          call("storeKayzenApiKey", {
                            text: val,
                            playableId: playable._id,
                          })
                            .then(() => {
                              setKayzenApiKeyAvailable(true);
                              download(version, selected, {}, true, null, true);
                            })
                            .catch((err) => {
                              message.error(
                                "Kayzen API Key could not be saved."
                              );
                            });
                        } else {
                          download(version, selected, {}, true, null, true);
                        }
                      }}
                      defaultValue={playable?.kayzenApiKey}
                      requireValue={kayzenApiKeyAvailable}
                    />
                  )}

                  {selected === "multi" && (
                    <DownloadButton onClick={multidownload}>
                      Download
                    </DownloadButton>
                  )}

                  {map[selected]?.indexOf("copy") >= 0 &&
                    playable &&
                    version?._id && (
                      <Clipboard
                        text={copyLink}
                        beforeCopy={() => {
                          if (version) {
                            return createCopyLink(
                              version,
                              selected?.[1],
                              `var type = ${selected?.[1]}`,
                              false
                            );
                          } else return Promise.reject("Can't copy link before load");
                        }}
                        afterCopy={() => { message.info("Link copied", 1).then(/* do nothing */) }}
                        onError={(reason) => { message.error(reason, 1).then(/* do nothing */) }}
                      >
                        <div className="hovergrey2 flex items-center cursor-pointer bg-white h-[54px] w-[200px] p-2 ml-[30px] rounded-lg">
                          <CopyIcon className="fill-black	w-9 bg-[#c1c1c110] p-2.5 rounded-[10px]" />
                          <div className="w-[180px] ml-0 text-center	text-base text-black">
                            Copy Link
                          </div>
                        </div>
                      </Clipboard>
                    )}
                </div>
              )}
              {downloading && (
                <div className="flex items-center justify-center	h-[100px] border-t-2 border-r-0 border-l-0 border-b-0 border-[#c1c1c1c0]">
                  <div className="bg-[#f78645] h-[54px] w-[200px] p-3 rounded-lg	flex items-center justify-center cursor-pointer">
                    <Spin
                      indicator={
                        <LoadingOutlined className="text-white text-base" />
                      }
                    />
                    <span className="text-white text-base font-bold	ml-[10px]">
                      Creating Playable
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};
export default Download;

let PlatformButton = ({
  isSelected,
  adNetworks,
  isMulti,
  index,
  network,
  onClick,
}) => {
  return (
    <div
      className={`hovergrey2 shadows h-14 rounded-lg text-sm p-2 flex justify-center items-center relative cursor-pointer text-black ${isSelected ? "bg-[#f5ede9]" : "bg-white"
        }`}
      onClick={onClick}
    >
      <img
        src={"/" + network + ".png"}
        alt={network}
        className="h-[38px] w-[130px] object-contain"
      />
      {isMulti && (
        <Checkbox checked={isSelected} className={"absolute left-1.5"} />
      )}
      {dataTrackingNetworks.includes(network) ? (
        <div className="p-1 absolute left-full top-0 -translate-x-full">
          <StarFilled className="text-l text-[#2B5ECA]" />
        </div>
      ) : null}
    </div>
  );
};

Download.whyDidYouRender = true;

let DownloadMolocoTest = ({ versionId, type }) => {
  const [busy, setBusy] = useState();
  const [url, setUrl] = useState();
  const [source, setSource] = useState();

  const handleClick = useCallback(() => {
    if (!versionId) {
      return;
    }
    message.info("downloading");
    setBusy(true);
    const nano = nanoid(5);
    call("createPlayableLink", {
      _id: versionId,
      nano,
      network: "moloco",
      undefined,
      zip: false,
      analytics: true,
      additional: { molocoS3: true },
    })
      .then((res) => setUrl(res.url))
      .catch(() => setUrl(undefined));
  }, [versionId]);

  useEffect(() => {
    if (!url) {
      return;
    }
    fetch(window.host + "/files/" + url)
      .then((x) => x.text())
      .then((res) => setSource(res))
      .catch();
  }, [url]);

  useEffect(() => {
    if (!source || source.length === 0) return;
    const templates = {
      Applovin:
        '{"cur":"USD","id":"2be72885d7954bd8a731108d86d41b4abb2a2197","seatbid":[{"bid":[{"adid":"oOsBIGBokhmzLDk1.a.qd4LesSpgup13ArS","adm":"<VAST version=\\"3.0\\"><Ad id=\\"oOsBIGBokhmzLDk1.a.qd4LesSpgup13ArS\\"><InLine><AdSystem version=\\"2.0\\"></AdSystem><AdTitle><![CDATA[oOsBIGBokhmzLDk1.a.qd4LesSpgup13ArS]]></AdTitle><Impression id=\\"oOsBIGBokhmzLDk1.a.qd4LesSpgup13ArS\\"><![CDATA[https://tr-us.adsmoloco.com/rtb/imp_fwd?exchange=APPLOVIN&info=ChAt4vtb6EJNq4PhfVssmxi6ELqg0JsGGhQIARoQavi7965kS0m3_ezvTEDk1yABKgAyAA&price=${AUCTION_PRICE}]]></Impression><Creatives><Creative><Linear><Duration>00:00:30</Duration><TrackingEvents><Tracking event=\\"firstQuartile\\"><![CDATA[https://tr-us.adsmoloco.com/rtb/imp_extra?exchange=APPLOVIN&info=ChAt4vtb6EJNq4PhfVssmxi6ELqg0JsGGhQIARoQavi7965kS0m3_ezvTEDk1yABKgAyAA&event_type=play_1q]]></Tracking><Tracking event=\\"midpoint\\"><![CDATA[https://tr-us.adsmoloco.com/rtb/imp_extra?exchange=APPLOVIN&info=ChAt4vtb6EJNq4PhfVssmxi6ELqg0JsGGhQIARoQavi7965kS0m3_ezvTEDk1yABKgAyAA&event_type=play_2q]]></Tracking><Tracking event=\\"thirdQuartile\\"><![CDATA[https://tr-us.adsmoloco.com/rtb/imp_extra?exchange=APPLOVIN&info=ChAt4vtb6EJNq4PhfVssmxi6ELqg0JsGGhQIARoQavi7965kS0m3_ezvTEDk1yABKgAyAA&event_type=play_3q]]></Tracking><Tracking event=\\"complete\\"><![CDATA[https://tr-us.adsmoloco.com/rtb/imp_extra?exchange=APPLOVIN&info=ChAt4vtb6EJNq4PhfVssmxi6ELqg0JsGGhQIARoQavi7965kS0m3_ezvTEDk1yABKgAyAA&event_type=play_4q]]></Tracking></TrackingEvents><VideoClicks><ClickThrough><![CDATA[https://tr-us.adsmoloco.com/rtb/click?exchange=APPLOVIN&imp_id=2be72885d7954bd8a731108d86d41b4abb2a2197&info=ChAt4vtb6EJNq4PhfVssmxi6ELqg0JsGGhQIARoQavi7965kS0m3_ezvTEDk1yABKgAyAA&campaign_name=d6CJa3T9aYcc1yEx&dcr=]]></ClickThrough></VideoClicks><MediaFiles><MediaFile id=\\"oOsBIGBokhmzLDk1.a.qd4LesSpgup13ArS\\" delivery=\\"progressive\\" type=\\"video/mp4\\" width=\\"360\\" height=\\"640\\"><![CDATA[https://cdn-f.adsmoloco.com/king/creative/la2szjv7_m16ly8y_qdpyqh49wthqhksm.mp4]]></MediaFile></MediaFiles></Linear><CreativeExtensions></CreativeExtensions></Creative><Creative><CompanionAds><Companion id=\\"oOsBIGBokhmzLDk1.a.qd4LesSpgup13ArS\\" width=\\"320\\" height=\\"480\\" assetWidth=\\"0\\" assetHeight=\\"0\\" expandedWidth=\\"0\\" expandedHeight=\\"0\\"><TrackingEvents><Tracking event=\\"creativeView\\"><![CDATA[https://tr-us.adsmoloco.com/rtb/imp_extra?exchange=APPLOVIN&info=ChAt4vtb6EJNq4PhfVssmxi6ELqg0JsGGhQIARoQavi7965kS0m3_ezvTEDk1yABKgAyAA&event_type=endcard]]></Tracking></TrackingEvents><HTMLResource><![CDATA[#PLAYABLE_FACTORY_SOURCE]]></HTMLResource></Companion></CompanionAds><CreativeExtensions></CreativeExtensions></Creative></Creatives><Description></Description><Survey></Survey><Extensions></Extensions></InLine></Ad></VAST>","adomain":["king.com"],"attr":[6],"bundle":"com.king.candycrushsaga","burl":"https://tr-us.adsmoloco.com/rtb/imp?exchange=APPLOVIN&info=ChAt4vtb6EJNq4PhfVssmxi6ELqg0JsGGhQIARoQavi7965kS0m3_ezvTEDk1yABKgAyAA&campaign_name=d6CJa3T9aYcc1yEx&imp_id=2be72885d7954bd8a731108d86d41b4abb2a2197&price=${AUCTION_PRICE}","cat":["IAB9"],"cid":"d6CJa3T9aYcc1yEx","crid":"king:a:qd4LesSpgup13ArS","ext":{"crtype":"VAST 3.0","duration":30},"id":"1","impid":"1","lurl":"https://noti-us.adsmoloco.com/rtb/loss?code=${AUCTION_LOSS}&ctx=CgNVU0ESFgoSdXMuY3VycmVudC5hbmRyb2lkEAEYAiIQZDZDSmEzVDlhWWNjMXlFeA&exchange=APPLOVIN&force_stream=false&mtid=ChAt4vtb6EJNq4PhfVssmxi6ELqg0JsGGhQIARoQavi7965kS0m3_ezvTEDk1yABKgAyAA&price=${AUCTION_PRICE}","nurl":"https://noti-us.adsmoloco.com/rtb/win?ctx=CgNVU0ESFgoSdXMuY3VycmVudC5hbmRyb2lkEAEYAiIQZDZDSmEzVDlhWWNjMXlFeA&exchange=APPLOVIN&force_stream=false&mbtw=${AUCTION_MINIMUM_BID_TO_WIN}&mtid=ChAt4vtb6EJNq4PhfVssmxi6ELqg0JsGGhQIARoQavi7965kS0m3_ezvTEDk1yABKgAyAA&price=${AUCTION_PRICE}","price":0.012}],"seat":"s"}]}'.replace(
          "#PLAYABLE_FACTORY_SOURCE",
          JSON.stringify(source).slice(1, -1)
        ),
      Chartboost:
        '{"id":"94dedeadbeefc4b8fcba406639bbf15912766bdc","seatbid":[{"bid":[{"id":"1","impid":"1","price":0.07,"nurl":"https://noti-us.adsmoloco.com/rtb/win?ctx=CgNUU1QSFAoQY29tLmV4YW1wbGUuZ2FtZRABGAIiEEt5OUw2NGFhV1N6S3FjN1I&exchange=APPLOVIN&force_stream=true&mbtw=${AUCTION_MINIMUM_BID_TO_WIN}&mtid=ChAHYPmIxnBFqaZk5gGz5Sl3EPno35sGGhQIBRoQ3Dd1AATzQG6AAAAAAAAAACABKgIIATIA&price=${AUCTION_PRICE}","burl":"https://tr-us.adsmoloco.com/rtb/imp?exchange=APPLOVIN&info=ChAHYPmIxnBFqaZk5gGz5Sl3EPno35sGGhQIBRoQ3Dd1AATzQG6AAAAAAAAAACABKgIIATIA&campaign_name=Ky9L64aaWSzKqc7R&imp_id=94dedeadbeefc4b8fcba406639bbf15912766bdc&price=${AUCTION_PRICE}","lurl":"https://noti-us.adsmoloco.com/rtb/loss?code=${AUCTION_LOSS}&ctx=CgNUU1QSFAoQY29tLmV4YW1wbGUuZ2FtZRABGAIiEEt5OUw2NGFhV1N6S3FjN1I&exchange=APPLOVIN&force_stream=true&mtid=ChAHYPmIxnBFqaZk5gGz5Sl3EPno35sGGhQIBRoQ3Dd1AATzQG6AAAAAAAAAACABKgIIATIA&price=${AUCTION_PRICE}","adm":"<VAST version=\\"3.0\\"><Ad id=\\"oOsBIGBokhmzLDk1.a.qd4LesSpgup13ArS\\"><InLine><AdSystem version=\\"2.0\\"></AdSystem><AdTitle><![CDATA[oOsBIGBokhmzLDk1.a.qd4LesSpgup13ArS]]></AdTitle><Impression id=\\"oOsBIGBokhmzLDk1.a.qd4LesSpgup13ArS\\"><![CDATA[https://tr-us.adsmoloco.com/rtb/imp_fwd?exchange=APPLOVIN&info=ChAt4vtb6EJNq4PhfVssmxi6ELqg0JsGGhQIARoQavi7965kS0m3_ezvTEDk1yABKgAyAA&price=${AUCTION_PRICE}]]></Impression><Creatives><Creative><Linear><Duration>00:00:30</Duration><TrackingEvents><Tracking event=\\"firstQuartile\\"><![CDATA[https://tr-us.adsmoloco.com/rtb/imp_extra?exchange=APPLOVIN&info=ChAt4vtb6EJNq4PhfVssmxi6ELqg0JsGGhQIARoQavi7965kS0m3_ezvTEDk1yABKgAyAA&event_type=play_1q]]></Tracking><Tracking event=\\"midpoint\\"><![CDATA[https://tr-us.adsmoloco.com/rtb/imp_extra?exchange=APPLOVIN&info=ChAt4vtb6EJNq4PhfVssmxi6ELqg0JsGGhQIARoQavi7965kS0m3_ezvTEDk1yABKgAyAA&event_type=play_2q]]></Tracking><Tracking event=\\"thirdQuartile\\"><![CDATA[https://tr-us.adsmoloco.com/rtb/imp_extra?exchange=APPLOVIN&info=ChAt4vtb6EJNq4PhfVssmxi6ELqg0JsGGhQIARoQavi7965kS0m3_ezvTEDk1yABKgAyAA&event_type=play_3q]]></Tracking><Tracking event=\\"complete\\"><![CDATA[https://tr-us.adsmoloco.com/rtb/imp_extra?exchange=APPLOVIN&info=ChAt4vtb6EJNq4PhfVssmxi6ELqg0JsGGhQIARoQavi7965kS0m3_ezvTEDk1yABKgAyAA&event_type=play_4q]]></Tracking></TrackingEvents><VideoClicks><ClickThrough><![CDATA[https://tr-us.adsmoloco.com/rtb/click?exchange=APPLOVIN&imp_id=2be72885d7954bd8a731108d86d41b4abb2a2197&info=ChAt4vtb6EJNq4PhfVssmxi6ELqg0JsGGhQIARoQavi7965kS0m3_ezvTEDk1yABKgAyAA&campaign_name=d6CJa3T9aYcc1yEx&dcr=]]></ClickThrough></VideoClicks><MediaFiles><MediaFile id=\\"oOsBIGBokhmzLDk1.a.qd4LesSpgup13ArS\\" delivery=\\"progressive\\" type=\\"video/mp4\\" width=\\"360\\" height=\\"640\\"><![CDATA[https://cdn-f.adsmoloco.com/king/creative/la2szjv7_m16ly8y_qdpyqh49wthqhksm.mp4]]></MediaFile></MediaFiles></Linear><CreativeExtensions></CreativeExtensions></Creative><Creative><CompanionAds><Companion id=\\"oOsBIGBokhmzLDk1.a.qd4LesSpgup13ArS\\" width=\\"320\\" height=\\"480\\" assetWidth=\\"0\\" assetHeight=\\"0\\" expandedWidth=\\"0\\" expandedHeight=\\"0\\"><TrackingEvents><Tracking event=\\"creativeView\\"><![CDATA[https://tr-us.adsmoloco.com/rtb/imp_extra?exchange=APPLOVIN&info=ChAt4vtb6EJNq4PhfVssmxi6ELqg0JsGGhQIARoQavi7965kS0m3_ezvTEDk1yABKgAyAA&event_type=endcard]]></Tracking></TrackingEvents><HTMLResource><![CDATA[#PLAYABLE_FACTORY_SOURCE]]></HTMLResource></Companion></CompanionAds><CreativeExtensions></CreativeExtensions></Creative></Creatives><Description></Description><Survey></Survey><Extensions></Extensions></InLine></Ad></VAST>","adid":"testAdid","adomain":["farmheroessaga.com"],"bundle":"com.king.farmheroessaga","cid":"Ky9L64aaWSzKqc7R","crid":"KING:a:qd4LesSpgup13ArS","cat":["IAB9-30"],"ext":{"duration":30,"crtype":"VAST 3.0"}}],"seat":"s"}],"cur":"USD"}'.replace(
          "PLAYABLE_FACTORY_SOURCE",
          JSON.stringify(source).slice(1, -1)
        ),
    }; // Source is not available cancel everything;w
    let element = document.createElement("a");
    element.setAttribute(
      "href",
      "data:application/json;charset=utf-8," +
      encodeURIComponent(templates[type])
    );
    element.setAttribute("download", url.split("/").at(-1) + ".json");

    element.style.display = "none";
    element.onclick = () => {
      document.body.removeChild(element);
    };
    document.body.appendChild(element);
    element.click();
    setBusy(false);
  }, [source, type]);

  return (
    <div
      className="hoverorange bg-[#f78645] h-[54px] w-[200px] p-2 ml-2.5	rounded-lg flex items-center cursor-pointer"
      onClick={handleClick}
    >
      <DownloadsIcon className="fill-[#f78645] bg-white w-9 p-2 rounded-[10px]" />
      <div className="w-[180px] ml-0 text-center	text-base	text-white font-bold">
        {busy ? "Generating" : `Download Moloco Test(${type})`}
      </div>
    </div>
  );
};

let DownloadButton = ({ onClick, children }) => {
  return (
    <div
      className="hoverorange bg-[#f78645] h-[54px] w-[200px] p-2 ml-2.5	rounded-lg flex items-center cursor-pointer"
      onClick={onClick}
    >
      <DownloadsIcon className="fill-[#f78645] bg-white w-9 p-2 rounded-[10px]" />
      <div className="w-[180px] ml-0 text-center	text-base	text-white font-bold">
        {children}
      </div>
    </div>
  );
};
let UploadButton = ({ onClick, children }) => {
  return (
    <div
      className="hoverorange bg-[#f78645] h-[54px] w-[200px] p-2 ml-2.5	rounded-lg flex items-center cursor-pointer"
      onClick={onClick}
    >
      <CloudUploadOutlined className="fill-[#f78645] text-[#f78645] bg-white w-9 p-2 rounded-[10px]" />
      <div className="w-[180px] ml-0 text-center	text-base	text-white font-bold">
        {children}
      </div>
    </div>
  );
};

let InputButton = (props) => {
  const [edit, setEdit] = useState(false);
  let [apiKey, setApiKey] = useState();
  let onApiKeyChange = useCallback((e) => {
    let val = e.target.value;
    setApiKey(val);
  }, []);
  let _onClick = useCallback(() => {
    if (edit) {
      if (apiKey) {
        props.onClick(apiKey);
      }
    } else {
      props.onClick(apiKey);
    }
  }, [props.onClick, apiKey]);
  const handleEdit = useCallback(
    (e) => {
      e.stopPropagation();
      setEdit(true);
    },
    [setEdit]
  );

  return (
    <div
      className={
        "bg-pforange h-[54px] w-[200px] p-2 ml-[10px] rounded-lg flex items-center cursor-pointer select-none"
      }
      onClick={!edit && props.requireValue && _onClick}
    >
      {(edit || !props.requireValue) && (
        <>
          <div
            className={
              "flex items-center h-8 color-[#2f2c37] text-black border-solid border-2 border-[#ddd] rounded-[10px] bg-white p-1.5"
            }
          >
            <input
              name={"apikey"}
              autoComplete={true}
              defaultValue={props.defaultValue}
              onChange={onApiKeyChange}
              placeholder={"Kayzen Api Key"}
              className={"w-full border-none"}
            />
            <DownloadsIcon
              fill={
                (!edit && props.requireValue) || apiKey ? "#f78645" : "#ddd"
              }
              className={"w-4"}
              onClick={_onClick}
            />
          </div>
        </>
      )}
      {!edit && props.requireValue && (
        <>
          <DownloadsIcon className="fill-[#f78645] w-9 text-base	bg-white p-2 rounded-[10px]" />
          <div className="w-[180px] ml-0 text-center	text-base	text-white font-bold">
            Download
          </div>
          <div
            className={"h-8 border-solid border-white border-0 border-l p-2"}
          ></div>
          <Tooltip title={"Change Kayzen API Key"}>
            <div className={"bg-white flex item-center p-2 rounded-[10px]"}>
              <EditFilled className={"text-pf"} onClick={handleEdit} />
            </div>
          </Tooltip>
        </>
      )}
    </div>
  );
};

const useSelect = (initial) => {
  const [state, setState] = useState(initial);
  const handleChange = useCallback(setState, []);

  return [state, handleChange];
};

let ShowSelectionUnityCreative = ({
  playableId,
  gameId,
  defaultGoogleAppId,
  defaultAppleAppId,
  versionId,
  customerId,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [googleApps, setGoogleApps] = useState([]);
  const [appleApps, setAppleApps] = useState([]);
  const [selectedGoogleApp, handleOnChangeGoogleApp] =
    useSelect(defaultGoogleAppId);
  const [selectedAppleApp, handleOnChangeAppleApp] =
    useSelect(defaultAppleAppId);
  const [selectedLangauge, handleOnChangeLanguage] = useSelect();
  console.log("state", selectedAppleApp, selectedGoogleApp, selectedLangauge);
  const [hasApiKey, setHasApiKey] = useState();

  useEffect(() => {
    call("isApiKeyAvailable", { type: "unity" }).then((res) =>
      setHasApiKey(res.result)
    );
  }, [customerId]);

  useEffect(() => {
    if (isModalOpen) {
      getAppIds("google").then(setGoogleApps);
      getAppIds("apple").then(setAppleApps);
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (selectedAppleApp && selectedGoogleApp && playableId) {
      call("saveUnityApi", {
        _id: playableId,
        game: gameId,
        unityGoogleAppId: selectedGoogleApp,
        unityAppleAppId: selectedAppleApp,
      });
    }
  }, [selectedAppleApp, selectedGoogleApp, playableId]);

  const getAppIds = async (store) => {
    try {
      let response = await call("listUnityApps", {
        store: store,
      });
      return response?.result.map((res) => ({
        value: res.id,
        label: res.name,
      }));
    } catch (error) {
      console.log(error);
    }
  };
  const uploadToUnity = async () => {
    try {
      let response = await axios.post(
        host + "/api/uploadToUnity",
        {
          versionId: versionId,
          language: selectedLangauge,
        },
        {
          headers: {
            Authorization: localStorage.getItem("token"),
          },
        }
      );
      message.success("Sent to Unity");
      return response;
    } catch (error) {
      console.log(error);
    }
  };

  const showModal = () => {
    if (hasApiKey) {
      setIsModalOpen(true);
    } else {
      setIsErrorModalOpen(true);
    }
  };
  const handleOk = async () => {
    await uploadToUnity();
    setIsModalOpen(false);
  };
  const handleCancel = () => {
    setIsModalOpen(false);
  };
  const handleErrorModalCancel = () => {
    setIsErrorModalOpen(false);
  };
  const handleErrorModalOk = async () => {
    setIsErrorModalOpen(false);
  };

  let unityLanguage = [
    { value: "und", label: "Undetermined" },

    { value: "af", label: "Afrikaans" },

    { value: "ar", label: "Arabic" },
    {
      value: "as",
      label: "Assamese",
    },
    { value: "az", label: "Azerbaijani" },

    { value: "be", label: "Belarusian" },
    {
      value: "bn",
      label: "Bengali",
    },

    { value: "bg", label: "Bulgarian" },

    { value: "ca", label: "Catalan" },

    { value: "zh", label: "Chinese" },
    {
      value: "hr",
      label: "Croatian",
    },
    {
      value: "cs",
      label: "Czech",
    },

    { value: "da", label: "Danish" },

    { value: "nl", label: "Dutch" },
    { value: "en", label: "English" },
    {
      value: "et",
      label: "Estonian",
    },
    { value: "fil", label: "Filipino" },
    { value: "fi", label: "Finnish" },

    { value: "fr", label: "French" },

    { value: "de", label: "German" },

    { value: "el", label: "Modern Greek (1453-)" },

    { value: "he", label: "Hebrew" },

    { value: "hi", label: "Hindi" },
    {
      value: "hu",
      label: "Hungarian",
    },

    { value: "is", label: "Icelandic" },

    { value: "id", label: "Indonesian" },
    {
      value: "it",
      label: "Italian",
    },
    {
      value: "ja",
      label: "Japanese",
    },
    {
      value: "kk",
      label: "Kazakh",
    },

    { value: "ko", label: "Korean" },

    { value: "ky", label: "Kirghiz" },
    {
      value: "lv",
      label: "Latvian",
    },
    {
      value: "lt",
      label: "Lithuanian",
    },
    {
      value: "mk",
      label: "Macedonian",
    },

    { value: "ms", label: "Malay (macrolanguage)" },

    { value: "mr", label: "Marathi" },

    { value: "mn", label: "Mongolian" },

    { value: "ne", label: "Nepali (macrolanguage)" },

    { value: "no", label: "Norwegian" },
    {
      value: "ps",
      label: "Pushto",
    },
    { value: "fa", label: "Persian" },

    { value: "pl", label: "Polish" },
    {
      value: "pt",
      label: "Portuguese",
    },

    { value: "ro", label: "Romanian" },

    { value: "ru", label: "Russian" },

    { value: "sa", label: "Sanskrit" },
    {
      value: "sr",
      label: "Serbian",
    },

    { value: "sk", label: "Slovak" },

    { value: "sl", label: "Slovenian" },
    {
      value: "es",
      label: "Spanish",
    },

    { value: "sv", label: "Swedish" },

    { value: "ta", label: "Tamil" },
    {
      value: "th",
      label: "Thai",
    },
    {
      value: "tr",
      label: "Turkish",
    },
    {
      value: "uk",
      label: "Ukrainian",
    },

    { value: "ur", label: "Urdu" },

    { value: "uz", label: "Uzbek" },
    { value: "vi", label: "Vietnamese" },
  ];

  return (
    <>
      <UploadButton onClick={showModal}>Upload</UploadButton>

      <Modal
        title="Unity Creative Options"
        visible={isModalOpen}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        centered
      >
        <div className="grid grid-cols-2 gap-4">
          <div>IOS App ID</div>
          <Select
            onChange={handleOnChangeAppleApp}
            value={selectedAppleApp}
            options={appleApps}
          />
          <div>Android App ID</div>
          <Select
            onChange={handleOnChangeGoogleApp}
            value={selectedGoogleApp}
            options={googleApps}
          />
          <div>Language</div>
          <Select onChange={handleOnChangeLanguage} options={unityLanguage} />
        </div>
      </Modal>
      <Modal
        visible={isErrorModalOpen}
        onOk={handleErrorModalOk}
        onCancel={handleErrorModalCancel}
        centered
      >
        <Text>
          Please set Unity Api Credentials in{" "}
          <a href="/profile" target="_blank">
            profile
          </a>{" "}
          page.
        </Text>
      </Modal>
    </>
  );
};

let ShowSelectionApplovinCreative = ({
  playableId,
  gameId,
  versionId,
  defaultAppleLink,
  defaultGoogleLink,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedGoogleLink, handleOnChangeGoogleLink] = useSelect();
  const [selectedAppleLink, handleOnChangeAppleLink] = useSelect();
  const [selectedLangauge, handleOnChangeLanguage] = useSelect([]);
  const [selectedTracking, handleOnChangeTracking] = useSelect();

  useEffect(() => {
    call("putPlayable", {
      _id: playableId,
      game: gameId,
      applovinGoogleLink: selectedGoogleLink,
      applovinAppleLink: selectedAppleLink,
    });
  }, [selectedAppleLink, selectedGoogleLink]);

  useEffect(() => {
    call("putPlayable", {
      _id: playableId,
      game: gameId,
    });
  }, []);

  const uploadToApplovin = async () => {
    try {
      let response = await call("uploadToApplovin", {
        versionId: versionId,
        languages: selectedLangauge,
        tracking: selectedTracking,
      });
      message.success("Sent to Applovin");
      return response;
    } catch (error) {
      console.log(error);
    }
  };
  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleOk = async () => {
    await uploadToApplovin();
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  let applovinLanguage = [
    { value: "und", label: "Undetermined" },

    { value: "af", label: "Afrikaans" },

    { value: "ar", label: "Arabic" },
    {
      value: "as",
      label: "Assamese",
    },
    { value: "az", label: "Azerbaijani" },

    { value: "be", label: "Belarusian" },
    {
      value: "bn",
      label: "Bengali",
    },

    { value: "bg", label: "Bulgarian" },

    { value: "ca", label: "Catalan" },

    { value: "zh", label: "Chinese" },
    {
      value: "hr",
      label: "Croatian",
    },
    {
      value: "cs",
      label: "Czech",
    },

    { value: "da", label: "Danish" },

    { value: "nl", label: "Dutch" },
    { value: "en", label: "English" },
    {
      value: "et",
      label: "Estonian",
    },
    { value: "fil", label: "Filipino" },
    { value: "fi", label: "Finnish" },

    { value: "fr", label: "French" },

    { value: "de", label: "German" },

    { value: "el", label: "Modern Greek (1453-)" },

    { value: "he", label: "Hebrew" },

    { value: "hi", label: "Hindi" },
    {
      value: "hu",
      label: "Hungarian",
    },

    { value: "is", label: "Icelandic" },

    { value: "id", label: "Indonesian" },
    {
      value: "it",
      label: "Italian",
    },
    {
      value: "ja",
      label: "Japanese",
    },
    {
      value: "kk",
      label: "Kazakh",
    },

    { value: "ko", label: "Korean" },

    { value: "ky", label: "Kirghiz" },
    {
      value: "lv",
      label: "Latvian",
    },
    {
      value: "lt",
      label: "Lithuanian",
    },
    {
      value: "mk",
      label: "Macedonian",
    },

    { value: "ms", label: "Malay (macrolanguage)" },

    { value: "mr", label: "Marathi" },

    { value: "mn", label: "Mongolian" },

    { value: "ne", label: "Nepali (macrolanguage)" },

    { value: "no", label: "Norwegian" },
    {
      value: "ps",
      label: "Pushto",
    },
    { value: "fa", label: "Persian" },

    { value: "pl", label: "Polish" },
    {
      value: "pt",
      label: "Portuguese",
    },

    { value: "ro", label: "Romanian" },

    { value: "ru", label: "Russian" },

    { value: "sa", label: "Sanskrit" },
    {
      value: "sr",
      label: "Serbian",
    },

    { value: "sk", label: "Slovak" },

    { value: "sl", label: "Slovenian" },
    {
      value: "es",
      label: "Spanish",
    },

    { value: "sv", label: "Swedish" },

    { value: "ta", label: "Tamil" },
    {
      value: "th",
      label: "Thai",
    },
    {
      value: "tr",
      label: "Turkish",
    },
    {
      value: "uk",
      label: "Ukrainian",
    },

    { value: "ur", label: "Urdu" },

    { value: "uz", label: "Uzbek" },
    { value: "vi", label: "Vietnamese" },
  ];

  let trackingMethod = [
    { value: "adjust", label: "Adjust" },
    { value: "appsflyer", label: "AppsFlyer" },
    { value: "tenjin", label: "Tenjin" },
    { value: "apsalar", label: "Singular" },
    { value: "applovin", label: "AppLovin" },
    { value: "kochava", label: "Kochava" },
    { value: "custom_track", label: "Custom" },
    { value: "justtrack", label: "JustTrack" },
    { value: "branch", label: "Branch" },
  ];

  return (
    <>
      <UploadButton onClick={showModal}>Upload</UploadButton>

      <Modal
        title="Applovin Creative Options"
        visible={isModalOpen}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        centered
      >
        <div className="grid grid-cols-2 gap-4">
          <div>IOS Link</div>
          <Input
            onChange={(e) => handleOnChangeAppleLink(e.target.value)}
            placeholder={defaultAppleLink}
          />
          <div>Android Link</div>
          <Input
            onChange={(e) => handleOnChangeGoogleLink(e.target.value)}
            placeholder={defaultGoogleLink}
          />
          <div>Tracking</div>
          <Select onChange={handleOnChangeTracking} options={trackingMethod} />
          <div>Language</div>
          <Select
            mode="multiple"
            onChange={handleOnChangeTracking}
            optionLabelProp="label"
          >
            {applovinLanguage.map((language) => (
              <Select.Option value={language.value} label={language.label}>
                <div className="demo-option-label-item">{language.label}</div>
              </Select.Option>
            ))}
          </Select>
        </div>
      </Modal>
    </>
  );
};
